import { fmtMoney } from "@/lib/format";
import { ExternalLink, TrendingDown, SearchX, Loader2, Info } from "lucide-react";
import { GoodCheck } from "@/components/GoodCheck";

export const ListingsCard = ({ result }) => {
  const listings = result.listings_found || [];
  const mismatch = result.price_mismatch_detected;
  const searching = result.search_in_progress;
  const prices = listings.map((l) => l.price);
  const allAgree = listings.length > 1 && prices.every((p) => Math.abs(p - prices[0]) < 1);
  const hasPriceDifference = listings.length > 1 && !allAgree;
  const minPrice = prices.length ? Math.min(...prices) : null;
  const sitesChecked = result.sites_checked;

  // Nothing to show a not-found message for — if no listings were located and we're not still searching,
  // hide the Advertised Price Check entirely rather than showing a "not found" notice.
  if (!searching && listings.length === 0) return null;

  return (
    <div data-testid="listings-card" className="rounded-xl border border-border bg-card p-6">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">
          Advertised Price Check
        </h3>
      </div>

      {/* search status: in-progress vs complete */}
      {searching ? (
        <div
          data-testid="search-progress"
          className="mb-4 flex items-start gap-2.5 rounded-lg border border-primary/20 bg-secondary/60 px-3 py-2.5"
        >
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden="true" />
          <span className="text-xs font-600 leading-relaxed text-foreground">
            {result.search_status || "Checking the dealer's website…"}
          </span>
        </div>
      ) : result.search_status && listings.length === 0 ? (
        <div
          data-testid="search-unconfirmed"
          className="mb-4 flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2.5"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <span className="text-xs font-600 leading-relaxed text-muted-foreground">{result.search_status}</span>
        </div>
      ) : null}

      {mismatch && (
        <div
          data-testid="price-mismatch-alert"
          className="mb-4 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive-soft p-4"
        >
          <TrendingDown className="mt-0.5 h-6 w-6 shrink-0 text-destructive" aria-hidden="true" />
          <div className="font-heading text-lg font-800 leading-snug text-destructive">
            Advertised price is {fmtMoney(result.discrepancy_amount)} less than this contract
          </div>
        </div>
      )}

      {listings.length === 0 ? (
        searching ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-secondary/50 px-4 py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            <p className="mt-3 text-sm font-600 text-foreground">Pulling the dealer's advertised price…</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
              We're locating this exact vehicle's advertised price. Verified
              prices will appear here as soon as we confirm them on the page.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-secondary/50 px-4 py-8 text-center">
            <SearchX className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <p className="mt-3 text-sm font-600 text-foreground">Vehicle advertisement not found</p>
            <p className="mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
              Cannot compare price.
            </p>
          </div>
        )
      ) : (
        <ul className="space-y-3">
          {listings.map((l, i) => {
            const isLowest = hasPriceDifference && minPrice != null && Math.abs(l.price - minPrice) < 1;
            return (
            <li
              key={l.url || `${l.source}-${i}`}
              data-testid={`listing-${i}`}
              className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition-colors duration-200 ${
                isLowest
                  ? "border-success/50 bg-success-soft"
                  : "border-border hover:bg-secondary"
              }`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-plex text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                    {l.source}
                  </span>
                  {isLowest && (
                    <span
                      data-testid={`listing-lowest-badge-${i}`}
                      className="flex items-center gap-1 rounded-full border border-success/40 bg-success/10 px-2 py-0.5 font-mono-plex text-[10px] font-700 uppercase tracking-[0.12em] text-success"
                    >
                      <TrendingDown className="h-3 w-3" aria-hidden="true" /> Lowest
                    </span>
                  )}
                </div>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`listing-link-${i}`}
                  className="flex items-center gap-1.5 truncate text-sm font-600 text-primary hover:underline"
                >
                  View listing <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                </a>
                {l.price_verified && (
                  <div className="mt-0.5 flex items-center gap-1 text-[10px] font-600 text-success">
                    <GoodCheck className="h-4 w-4" /> Price verified on page
                  </div>
                )}
              </div>
              <span className={`font-mono-plex text-base font-700 ${isLowest ? "text-success" : "text-foreground"}`}>
                {fmtMoney(l.price)}
              </span>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
